/**
 * Language picker: lists the supported UI languages by endonym and switches the
 * app locale through Refine's i18n provider. Used in the top bar (dark header) and
 * on the unauthenticated auth pages. `compact` renders icon-only for the narrow
 * toolbar; `onLight` recolors it for a light background (the auth pages).
 */
import React, { useState } from "react";

import { useTranslation } from "@refinedev/core";

import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";

import { Icon } from "@/components/engage";
import { LOCALE_ENDONYMS, SUPPORTED_LOCALES, type SupportedLocale, resolveSupportedLocale } from "@/utils/i18n-locale";

import { faLanguage } from "@fortawesome/free-solid-svg-icons";

interface LanguageSwitcherProps {
  /** Icon-only rendering for the narrow toolbar. */
  compact?: boolean;
  /** Recolor for a light background (e.g. the auth pages) instead of the dark header. */
  onLight?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false, onLight = false }) => {
  const { translate, changeLocale, getLocale } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const active = resolveSupportedLocale(getLocale());
  const label = translate("buttons.language");

  // Foreground sits on the teal header (contrastText) or on a light page (primary).
  const fg = onLight ? "primary.main" : "primary.contrastText";

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSelect = (lng: SupportedLocale) => {
    changeLocale(lng);
    handleClose();
  };

  return (
    <>
      {compact ? (
        <Tooltip title={label}>
          <IconButton
            aria-label={label}
            onClick={handleOpen}
            sx={{
              border: theme => `1px solid ${onLight ? theme.palette.primary.main : theme.palette.primary.contrastText}`,
              backgroundColor: onLight ? "transparent" : "primary.dark",
              width: 35,
              height: 35,
              "&:hover": {
                backgroundColor: onLight ? "action.hover" : "primary.main",
              },
            }}
          >
            <Icon name={faLanguage} color={fg} size="sm" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          onClick={handleOpen}
          // Include the visible endonym so the accessible name contains the shown label (WCAG 2.5.3).
          aria-label={`${label}: ${LOCALE_ENDONYMS[active]}`}
          startIcon={<Icon name={faLanguage} color={fg} size="sm" />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            color: fg,
            textTransform: "none",
            "&:hover": {
              backgroundColor: onLight ? "action.hover" : "primary.light",
            },
          }}
        >
          <Typography variant="body1">{LOCALE_ENDONYMS[active]}</Typography>
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 160, mt: 1 } } }}
      >
        {SUPPORTED_LOCALES.map(lng => (
          <MenuItem
            key={lng}
            role="menuitemradio"
            aria-checked={lng === active}
            selected={lng === active}
            onClick={() => handleSelect(lng)}
          >
            <ListItemText>
              <Typography variant="body1">{LOCALE_ENDONYMS[lng]}</Typography>
            </ListItemText>
            {lng === active && (
              <Box component="span" display="inline-flex" ml={1}>
                <CheckIcon fontSize="small" />
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
